import {
  createContext,
  ReactNode,
  useRef,
  useMemo,
  useContext,
  useCallback,
} from 'react';
import debounce from 'lodash/debounce';

import {
  ACTIVITIES_ANSWER_DRAW_TYPE,
  ACTIVITIES_ANSWER_TEXT,
} from 'global/constants';
import { useGetInteractiveLessonParams } from 'approot/activity-builder/activity-builder.hooks';
import { useSnackbar } from 'approot/shared/snackbar-notification/snackbar-notification.hooks';
import { useLoggingMutation } from 'lib/apollo/apollo-hooks';
import type { AnswerFragment } from '../../present/activity-answers.graphql.generated';

import {
  GET_MY_ANSWERS,
  UPSERT_TEACHER_ACTIVITY_ANSWER,
} from 'approot/activity-builder/present/activity-answers.graphql';

import type {
  GetMyAnswersQuery,
  GetMyAnswersQueryVariables,
} from '../../present/activity-answers.graphql.generated';
import { removeTypename } from 'approot/shared/logic/remove-typename';
import type {
  UpsertTeacherActivityAnswerMutation,
  UpsertTeacherActivityAnswerMutationVariables,
} from '../../present/activity-answers.graphql.generated';
import { upsertAnswerInCache } from 'approot/activity-builder/present/activity-answer.apollo';
import type { DrawAnswerFragment } from '../../present/activity-answers.graphql.generated';
import { useActivityBuilderRequest } from 'approot/activity-builder/activity-builder-request.apollo';

export type AnswerInput = AnswerFragment;

export type AnswerUpdateCallback = (answer: AnswerInput) => void;

type UpdateProps = {
  input: AnswerInput;
  variables: GetMyAnswersQueryVariables;
};

type BatchUpdate = {
  loading: boolean;
  updateAnswerApollo: (props: UpdateProps) => void;
  updateAnswer: (props: UpdateProps) => void;
};

export const BatchAnswersContext = createContext<BatchUpdate>({
  loading: false,
  updateAnswer: () => {},
  updateAnswerApollo: () => {},
});

export const BatchAnswersContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { showError } = useSnackbar();
  const answerSyncMapRef = useRef<Map<string, Function>>(new Map());

  const { contentId, contentType, version } = useGetInteractiveLessonParams();
  const { createRequest, clearRequest } = useActivityBuilderRequest();

  const [upsertAnswer, { loading, client }] = useLoggingMutation<
    UpsertTeacherActivityAnswerMutation,
    UpsertTeacherActivityAnswerMutationVariables
  >(UPSERT_TEACHER_ACTIVITY_ANSWER);

  const syncToServer = useCallback(
    async ({
      input,
      variables,
      requestId,
      onComplete,
    }: {
      input: AnswerInput;
      variables: GetMyAnswersQueryVariables;
      requestId: string;
      onComplete: (requestId: string) => void;
    }) => {
      if (!version) return;

      const answerProps =
        input.answerType === ACTIVITIES_ANSWER_DRAW_TYPE
          ? {
              __typename: 'DrawAnswer',
              answerType: ACTIVITIES_ANSWER_DRAW_TYPE,
              drawing: (input.answer as DrawAnswerFragment).drawing,
            }
          : input.answer;

      await upsertAnswer({
        fetchPolicy: 'no-cache',
        ignoreResults: true,
        variables: {
          input: {
            classId: variables.input.classId,
            contentType,
            contentId,
            componentId: input.componentId,
            contentVersion: version,
            uuid: input.uuid,
            activityId: input.activityId,
            answerReferences: input.answerReferences
              ? removeTypename(input.answerReferences)
              : null,
            answer: {
              ...removeTypename(answerProps),
              answerType: input.answerType || ACTIVITIES_ANSWER_TEXT,
            },
          },
        },
      })
        .catch(() => {
          showError(
            'We had trouble saving your data. Please refresh and check your answers.'
          );
        })
        .finally(() => {
          onComplete(requestId);
        });
    },
    [contentId, contentType, version, upsertAnswer, showError]
  );

  const updateAnswerApollo = useCallback(
    ({
      input,
      variables,
    }: {
      input: AnswerInput;
      variables: GetMyAnswersQueryVariables;
    }) => {
      const answersData = client.readQuery<GetMyAnswersQuery>({
        query: GET_MY_ANSWERS,
        variables,
      });
      upsertAnswerInCache({
        answer: input,
        answers: answersData?.getMyAnswers || [],
        client,
        variables,
      });

      if (!answerSyncMapRef.current.get(input.uuid)) {
        // create a new debounce function for the answer
        answerSyncMapRef.current.set(
          input.uuid,
          debounce(input => {
            const requestId = createRequest(input.componentId);
            syncToServer({
              input,
              variables,
              requestId,
              onComplete: (requestId: string) => {
                clearRequest(requestId);
              },
            });
          }, 300)
        );
      }

      // call the debounced function for this answer
      const sync = answerSyncMapRef.current.get(input.uuid);
      if (sync) {
        sync(input);
      }
    },
    [client, clearRequest, createRequest, syncToServer]
  );

  const updateAnswer = useCallback(
    ({ input, variables }: UpdateProps) => {
      if (!input) return;

      updateAnswerApollo({
        input,
        variables,
      });
    },
    [updateAnswerApollo]
  );

  const value = useMemo(
    () => ({
      loading,
      updateAnswer,
      updateAnswerApollo,
    }),
    [loading, updateAnswer, updateAnswerApollo]
  );

  return (
    <BatchAnswersContext.Provider value={value}>
      {children}
    </BatchAnswersContext.Provider>
  );
};

export const useBatchAnswersContext = () => {
  const { loading, updateAnswer, updateAnswerApollo } =
    useContext(BatchAnswersContext);

  const handleUpdate = useCallback(
    (props: UpdateProps) => {
      updateAnswer(props);
    },
    [updateAnswer]
  );

  return {
    loading,
    handleUpdate,
    updateAnswer,
    updateAnswerApollo,
  };
};
